/**
 * Row Drag-Drop Plugin CSS
 *
 * Styles for drag handle, drop indicators (intra- and cross-grid), grid-level
 * drag/drop state classes, and FLIP animation on landing rows.
 */

@layer tbw-plugins {
  /* ------------------------------------------------------------------ */
  /* Drag handle column                                                 */
  /* ------------------------------------------------------------------ */
  [data-field='__tbw_row_drag'] {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dg-row-drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1em;
    min-height: 1em;
    cursor: grab;
    user-select: none;
    color: var(--tbw-row-reorder-handle-color, var(--tbw-color-fg-muted));
    transition: color var(--tbw-transition-duration, 120ms) var(--tbw-transition-ease, ease);
    font-size: var(--tbw-font-size, 1em);
    letter-spacing: -2px;

    /* SC 2.5.8: grow the target, not the box, so the row keeps its height. */
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset-block-start: 50%;
      inset-inline-start: 50%;
      translate: -50% -50%;
      inline-size: var(--tbw-touch-target-min, 24px);
      block-size: var(--tbw-touch-target-min, 24px);
    }

    &:hover {
      color: var(--tbw-row-reorder-handle-hover, var(--tbw-color-fg));
    }

    &:active {
      cursor: grabbing;
    }
  }

  /* Coarse pointer (touch): handle always in active colour and large enough to tap */
  @media (hover: none) {
    .dg-row-drag-handle {
      color: var(--tbw-row-reorder-handle-hover, var(--tbw-color-fg));
    }
  }

  @media (pointer: coarse) {
    .dg-row-drag-handle {
      min-width: var(--tbw-touch-target-min, 24px);
      min-height: var(--tbw-touch-target-min, 24px);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Source row state                                                   */
  /* ------------------------------------------------------------------ */
  .data-grid-row.dragging {
    opacity: 0.6;
  }

  /* When the row itself is the drag handle, hint at the affordance.    */
  .data-grid-row[draggable='true'] {
    cursor: grab;
  }

  .data-grid-row[draggable='true'].dragging {
    cursor: grabbing;
  }

  /* Off-screen full-row clone used as the HTML5 drag image.            */
  /* The browser already applies its own translucency to the snapshot,  */
  /* so this clone keeps full opacity and skips the box-shadow — both   */
  /* would otherwise composite on top of the browser's effect and make  */
  /* the drag image look washed-out / fade at the edges.                */
  .tbw-row-drag-clone {
    position: fixed;
    top: -10000px;
    left: -10000px;
    pointer-events: none;
  }

  /* Multi-row drag count badge */
  .tbw-row-drag-count {
    position: fixed;
    top: -1000px; /* off-screen until the browser snapshots it */
    left: -1000px;
    pointer-events: none;
    background: var(--tbw-row-reorder-indicator, var(--tbw-color-accent));
    color: var(--tbw-color-accent-fg);
    font:
      600 12px/1.4 system-ui,
      sans-serif;
    border-radius: 999px;
    padding: 2px 8px;
    box-shadow: 0 2px 6px var(--tbw-color-shadow);
    z-index: 9999;
  }

  /* ------------------------------------------------------------------ */
  /* Drop position indicator (rows under cursor)                        */
  /* ------------------------------------------------------------------ */
  .data-grid-row.drop-target {
    position: relative;

    &.drop-before::before,
    &.drop-after::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: var(--tbw-drop-indicator-width, 2px);
      background-color: var(--tbw-drop-indicator-color, var(--tbw-row-reorder-indicator, var(--tbw-color-accent)));
      z-index: 10;
      pointer-events: none;
    }

    &.drop-before::before {
      top: 0;
    }

    &.drop-after::after {
      bottom: 0;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Grid-level state classes                                           */
  /* ------------------------------------------------------------------ */

  /* Source grid while a drag originating here is in progress */
  .tbw-grid--drag-source {
    /* Reserved hook for consumer styling. */
  }

  /* Target grid while a valid drag hovers (matching dropZone, canDrop ok) */
  .tbw-grid--drop-target-active {
    outline: 2px dashed var(--tbw-drop-indicator-color, var(--tbw-color-accent));
    outline-offset: -2px;
  }

  /* Briefly applied when a drop attempt is rejected */
  .tbw-grid--drop-target-rejected {
    outline: 2px dashed var(--tbw-color-danger);
    outline-offset: -2px;
  }

  /* Applied while the auto-scroll engine is actively scrolling this grid */
  .tbw-grid--auto-scrolling {
    cursor: ns-resize;
  }

  /* ------------------------------------------------------------------ */
  /* FLIP animation on landing rows                                     */
  /* ------------------------------------------------------------------ */
  .data-grid-row.flip-animating {
    transition: transform var(--tbw-animation-duration, 200ms) ease-out;
    will-change: transform;
    z-index: 1;
  }

  /* ------------------------------------------------------------------ */
  /* Click-only move menu (WCAG 2.2 SC 2.5.7)                           */
  /* ------------------------------------------------------------------ */
  /* Rendered into `document.body` and promoted to the top layer, so it sits
     outside the grid's custom-property scope — every var needs a literal
     fallback here, the same way the tooltip popover does it. */
  .tbw-row-move-menu {
    position: fixed;
    margin: 0;
    display: flex;
    flex-direction: column;
    min-width: 10rem;
    padding: var(--tbw-spacing-xs, 0.25rem);
    background: var(--tbw-color-panel-bg, light-dark(#fff, #1f1f1f));
    color: var(--tbw-color-fg, light-dark(#1a1a1a, #f0f0f0));
    border: 1px solid var(--tbw-color-border, light-dark(#d0d0d0, #3a3a3a));
    border-radius: var(--tbw-border-radius, 0.25rem);
    box-shadow: var(--tbw-shadow-md, 0 4px 12px rgb(0 0 0 / 25%));
    font: inherit;
    z-index: 10001;

    &[hidden] {
      display: none;
    }
  }

  .tbw-row-move-menu-item {
    min-height: var(--tbw-touch-target-min, 24px);
    padding: var(--tbw-spacing-xs, 0.25rem) var(--tbw-spacing-sm, 0.5rem);
    text-align: start;
    background: none;
    border: none;
    /* Themes set radii for much larger surfaces (Material uses 12px), which
       turns a 24px row into a lozenge. */
    border-radius: min(var(--tbw-border-radius, 0.25rem), 6px);
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: var(--tbw-color-row-hover, light-dark(#f0f0f0, #2a2a2a));
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}
